import type { Styles } from '@adornis/ass/style.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { XSidebar, XSidebarItem, type SidebarRoutesInfo } from '@adornis/chemistry/elements/components/x-sidebar.js';
import { xComponents } from '@adornis/chemistry/elements/x-components.js';
import { type RouteConfig } from '@adornis/router/client/make-router.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { html, nothing, type PropertyValues, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '../fonts/fonts.js';
import './tr-icon.js';
import './tr-link.js';

@customElement('tr-sidebar')
export class TrSidebar extends XSidebar {}

@customElement('tr-sidebar-item')
export class TrSidebarItem extends XSidebarItem {
  @property({ type: Boolean }) minify!: boolean;
  @property({ type: Boolean }) activeSubitem: boolean = false;
  @property({ attribute: false }) subItems: Array<{ href: string; icon: string; text: string }> = [];

  @property({ attribute: false }) isOpen = true;

  override render() {
    return xComponents(html`
      <tr-link class=${this.active ? 'active-item item' : 'item'} href=${this.href || '#'} ${css({ color: 'unset' })}>
        <tr-flex w100>
          <tr-flex
            horizontal
            flex
            crossaxis-center
            space="sm"
            ${css({ padding: `${this.spacing.sm} ${this.spacing.md}` })}
          >
            ${this.icon ? this.iconDisplay() : nothing}
            <tr-text
              flex
              ${css({ color: this.active ? this.colors.accent.shade(700) : this.colors.tone.secondaryText })}
            >
              ${this.text}
            </tr-text>
            ${this.subItems.length > 0
              ? html`
                  <tr-icon
                    @click=${e => {
                      e.preventDefault();
                      e.stopPropagation();
                      this.isOpen = !this.isOpen;
                    }}
                  >
                    ${this.isOpen ? 'expand_less' : 'expand_more'}
                  </tr-icon>
                `
              : nothing}
          </tr-flex>

          ${this.subItems.length > 0 && this.isOpen
            ? html`
                <tr-flex
                  ${css({
                    marginTop: '10px',
                    paddingLeft: this.spacing.xl,
                    overflow: 'hidden',
                  })}
                >
                  ${this.subItems.map(
                    subItem =>
                      html`
                        <tr-sidebar-item class="subitem" text=${subItem.text} href=${subItem.href}></tr-sidebar-item>
                      `,
                  )}
                </tr-flex>
              `
            : nothing}
        </tr-flex>
      </tr-link>
    `);
  }

  protected override iconDisplay(): TemplateResult<1> {
    return html` <tr-icon big ?outlined=${!this.active}> ${this.icon ?? 'notdef'} </tr-icon> `;
  }

  override styles() {
    return [
      {
        ':host': {
          maxWidth: '220px',

          position: 'relative',
          textDecoration: 'none',
          fontSize: '1.375rem',
          fontWeight: this.active ? 'bold' : 'normal',
        },
        ':host(:hover)': {
          background: `linear-gradient(90deg, ${this.colors.accent.opacity(0.125)} 20%, rgba(255,255,255, 0) 100%)`,
        },
        '.subitem:before': {
          content: "''",
          position: 'absolute',
          left: '-20px',
          width: '10px',
          height: 'calc(100% + 48px)',
          bottom: '0',
          borderBottom: `2px solid ${this.colors.neutral.shade(50)}`,
          borderLeft: `2px solid ${this.colors.neutral.shade(50)}`,
          transform: 'translateY(calc(-50% + 26px))',
        },
        '.item': {
          textDecoration: 'none',
          position: 'relative',
          width: '100%',
          borderLeft: `${this.spacing.xxs} solid TRANSPARENT`,
          borderRadius: this.sizes.borderRadius,
          borderTopLeftRadius: '0',
          borderBottomLeftRadius: '0',

          '&:before': this.activeSubitem
            ? {
                content: "''",
                position: 'absolute',
                right: '0',
                top: '17px',
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                background: this.colors.accent,
              }
            : {},
        },
        '.active-item': {
          position: 'relative',
          background: this.colors.accent.opacity(0.2),
          borderColor: this.colors.accent.shade(700),
        },
      },
    ] satisfies Styles[];
  }
}

// ! hard copied due to composability issue with current chemistry
@customElement('tr-sidebar-routing-item')
export class TrSidebarRoutingItem extends TrSidebarItem {
  @state() routeConfig: Maybe<RouteConfig<SidebarRoutesInfo> & { name: string }> = null;
  @state() translation = new TranslationController<any>(this);

  protected override willUpdate(_changedProperties: PropertyValues) {
    super.willUpdate(_changedProperties);

    if (_changedProperties.has('routeConfig') && this.routeConfig) {
      this.href =
        this.routeConfig.metaData?.href ?? this.routeConfig.path.replace('*', '').replaceAll(/\:[^\:\/]+/g, '');
      this.icon = this.routeConfig.metaData?.icon;
      this.text = this.translation.translate('route', this.routeConfig.name) as string;
    }
  }
}
